// PARTNER-PORTÁL · Kezdőlap — a terv szerint
// (a partner-terv szerint — a MEGÉPÜLT állapot: `docs/foundations/design.md` §9).
//
// A LAP KÉRDÉSE: „Van-e valami, ami rám vár, és hol tudok jegyet nyitni?" Ezért a hero mondja meg egy
// mondatban, mi vár a partnerre, a csempék adják a számokat, a lista pedig a legutóbbi nyitott jegyeket.
//
// A LAP NÉGY CSEMPÉT ad (a terv `grid-4`-je: nyitott jegy · új válasz · üzenet és meghívó · lejárt számla),
// alatta a legutóbbi jegyek és jobb oldalon a pénzügyi/„válaszra vár" blokk. A csempe akkor NEM jelenik meg,
// ha a keret-adat `null`-t ad rá: az azt jelenti, NEM TUDJUK (a modul nem szolgálható ki) — a 0 azt állítaná,
// hogy megnéztük és nincs semmi.
import { addressName, PORTAL_PERMISSIONS, type PortalPermission } from '@atlas/shared';
import {
  ArrowTopRightOnSquareIcon,
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  InformationCircleIcon,
  PlusIcon,
  ReceiptPercentIcon,
  Square3Stack3DIcon,
  TicketIcon,
} from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { MeetingRsvpBadge } from '@/components/portal/meeting-rsvp-badge';
import { CardRow, CardRows } from '@/components/shared/card-rows';
import { EmptyState } from '@/components/shared/empty-state';
import { formatMoney } from '@/components/shared/money-block';
import { PanelCard } from '@/components/shared/panel-card';
import { TicketStatusBadge } from '@/components/tickets/ticket-status-badge';
import { TicketTypeBadge } from '@/components/tickets/ticket-type-badge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { portalKeys } from '@/features/portal/api';
import { usePortal, usePortalHead } from '@/features/portal/portal-context';
import { usePortalPermissions } from '@/features/portal/use-portal-permissions';
import { api } from '@/lib/api';
import { formatDateTime, formatRelativeTime } from '@/lib/format-datetime';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/portal/')({
  component: PortalHomePage,
});

/** Egy KPI-csempe. A `null` érték → a csempe meg sem jelenik (lásd a fájl-fejléc magyarázatát). */
interface Tile {
  key: string;
  value: number | null;
  label: string;
  Icon: typeof TicketIcon;
  tone: 'brand' | 'sky' | 'violet' | 'success' | 'danger' | 'neutral';
  /** Hova visz a csempe — a szám mellett mindig ott van, hol lehet vele foglalkozni. */
  to: '/portal/tickets' | '/portal/messages' | '/portal/invoices';
  /** A csempe jog-kapuja (nincs megadva → jogfüggetlen). */
  permission?: PortalPermission;
}

function PortalHomePage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { me, accountId } = usePortal();
  // Jog-kapuk (fiókonkénti `permissions`): amihez egyetlen fiókon sincs jog, annak nincs csempéje/blokkja/gombja
  // és lekérdezése sem (különben garantált 403-at kérnénk). A fal a BFF-ben van.
  const perms = usePortalPermissions();
  const canViewTickets = perms.has(PORTAL_PERMISSIONS.ticketsView);
  const canOpenTicket = perms.has(PORTAL_PERMISSIONS.ticketsWrite);
  const canViewBilling = perms.has(PORTAL_PERMISSIONS.billingView);
  // A HERO fiók-címkéje: „Minden fiók" scope-nál (vagy egyetlen fióknál) az EGYETLEN fiók, különben a
  // kiválasztott. Több fiók + „Minden fiók" esetén NINCS egy konkrét fiók — a `undefined` ezt jelenti.
  const account =
    accountId === null
      ? me?.accounts.length === 1
        ? me.accounts[0]
        : undefined
      : me?.accounts.find((a) => a.id === accountId);

  usePortalHead(
    {
      title: t('portal.nav.home'),
      subtitle: [account?.clientName, account?.name].filter(Boolean).join(' · '),
    },
    [account?.clientName, account?.name, t('portal.nav.home')],
  );

  // A LEGUTÓBBI nyitott jegyek — ugyanaz a lista-végpont, amit a jegy-lap használ (nincs második olvasás),
  // és UGYANAZZAL A FIÓK-SCOPE-PAL. MIÉRT KELLETT JAVÍTANI (user-lelet): a kártya scope NÉLKÜL
  // kérdezett, tehát szűkített nézetben MÁS fiókok jegyeit is mutatta — miközben a fölötte lévő KPI-csempe
  // („Folyamatban lévő jegyem") és a Jegyeim lap már szűkült. Ugyanaz a szám két helyen mást mondott, és a
  // felhasználó ezt hiányzó/hibás adatnak látja, nem szűrőnek. A kulcs is a scope-ot hordozza, különben a
  // fiók-váltás a cache-elt (idegen) listát mutatta volna.
  const recentQ = useQuery({
    queryKey: portalKeys.ticketList({
      tab: 'in_progress',
      q: '',
      account: accountId ?? '',
      type: '',
    }),
    enabled: canViewTickets,
    queryFn: () =>
      api.portal.tickets.query({
        query: {
          tab: 'in_progress',
          limit: 50,
          lang: i18n.language === 'en' ? 'en' : 'hu',
          ...(accountId !== null ? { accountId } : {}),
        },
      }),
  });
  const recent = recentQ.data?.status === 200 ? recentQ.data.body.tickets.slice(0, 3) : [];
  const unreadTickets = me?.counts.unreadTickets ?? 0;

  // A LAP JOBB OLDALA és az „olvasatlan üzenetek" blokk ugyanazokból a végpontokból él, mint a saját lapjuk —
  // ugyanaz a query-kulcs, tehát a lapváltás nem indít új kérést (a cache-t osztják).
  const servicesQ = useQuery({
    queryKey: portalKeys.services(accountId ?? undefined),
    enabled: canViewBilling,
    queryFn: () =>
      api.portal.services.query({
        query: {
          lang: i18n.language === 'en' ? 'en' : 'hu',
          // A „következő havi várható díj" a SCOPE-ra szűkül; „Minden fiók"-nál minden fiók tétele
          // összeadódik (devizánként) — üres blokk helyett aggregát.
          ...(accountId !== null ? { accountId } : {}),
        },
      }),
  });
  const services = servicesQ.data?.status === 200 ? servicesQ.data.body : undefined;

  const messagesQ = useQuery({
    queryKey: portalKeys.messageList(i18n.language === 'en' ? 'en' : 'hu', accountId),
    queryFn: () =>
      api.portal.messages.query({
        query: {
          lang: i18n.language === 'en' ? 'en' : 'hu',
          ...(accountId !== null ? { accountId } : {}),
        },
      }),
  });
  const messages = messagesQ.data?.status === 200 ? messagesQ.data.body : undefined;
  const unreadMessages = (messages?.unavailable === true ? [] : (messages?.messages ?? [])).filter(
    (m) => m.unread,
  );
  // A kezdőlap MINDEN közelgő megbeszélést mutat (nem csak a válaszra várókat — user-döntés):
  // az állapot SORONKÉNT látszik jelvényként, tehát a lista maga hordozza a „mit kell tennem" információt.
  // A végpont eleve közelgő eseményeket ad (a tegnapi naptól), tehát itt nincs további idő-szűrés.
  const meetings = messages?.unavailable === true ? [] : (messages?.meetings ?? []);

  // MEGSZÓLÍTÁS: a becenév, különben a keresztnév (a közös szabály), végül a teljes név
  const greetingName =
    (me && addressName({ nickname: me.identity.nickname, givenName: me.identity.givenName })) ??
    me?.identity.name ??
    '';
  const overdueInvoices = me?.counts.overdueInvoices ?? null;
  const messageBadge = me?.counts.unreadMessages ?? null;
  const tiles: Tile[] = [
    {
      key: 'openTickets',
      value: me?.counts.openTickets ?? null,
      label: t('portal.home.tiles.openTickets'),
      Icon: TicketIcon,
      tone: 'brand',
      to: '/portal/tickets',
      permission: PORTAL_PERMISSIONS.ticketsView,
    },
    {
      key: 'unreadTickets',
      value: me?.counts.unreadTickets ?? null,
      label: t('portal.home.tiles.unreadTickets'),
      Icon: ChatBubbleLeftRightIcon,
      tone: unreadTickets > 0 ? 'sky' : 'neutral',
      to: '/portal/tickets',
      permission: PORTAL_PERMISSIONS.ticketsView,
    },
    {
      key: 'messages',
      value: messageBadge,
      label: t('portal.home.tiles.messages'),
      Icon: EnvelopeIcon,
      tone: messageBadge !== null && messageBadge > 0 ? 'violet' : 'neutral',
      to: '/portal/messages',
    },
    {
      key: 'invoices',
      value: overdueInvoices,
      label: t('portal.home.tiles.overdueInvoices'),
      // LEJÁRT számlánál danger, egyébként success: a „0 lejárt" jó hír, és azt is látni kell.
      Icon: ReceiptPercentIcon,
      tone: overdueInvoices !== null && overdueInvoices > 0 ? 'danger' : 'success',
      to: '/portal/invoices',
      permission: PORTAL_PERMISSIONS.billingView,
    },
  ];

  // A HERO mondata: mi vár rám. A számot a keret-adat adja; nulla teendőnél megnyugtató mondat áll ott.
  const heroText =
    canViewTickets && unreadTickets > 0
      ? t('portal.home.waiting', { count: unreadTickets })
      : t('portal.home.allClear');

  return (
    // A SZEKCIÓ-KÖZ `space-y-4` (16px), nem `space-y-8` (32px) — user-döntés: a nagyobb köz a
    // sűrű kártya-rácson „széteső" képet adott. Így a szekciók közti FÜGGŐLEGES köz megegyezik a rácson
    // belüli sor-közzel (`gap-y-4`), tehát a lap egyetlen 16px-es ritmusra fut.
    <div className="space-y-4">
      {/* HERO — a partner-felület egyetlen „nagy" felülete: 30px cím + radiális brand-fény + két CTA. Az
          admin kezdőlapja szándékosan sűrű, ott a hero helyet lopna (design-delta §4). */}
      <section className="border-border bg-card relative overflow-hidden rounded-2xl border px-6 py-8 md:px-8 md:py-8">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(620px_260px_at_88%_-30%,rgb(var(--brand)/0.16),transparent_66%)]"
        />
        <div className="relative">
          <h1 className="text-[23px] font-semibold leading-tight tracking-[-0.028em] md:text-[30px]">
            {/* A BECENÉV / KERESZTNÉV a barátságos megszólítás, de nem garantált (a token nem mindig hordoz
                `given_name`-et). Sorrend: becenév → keresztnév → teljes név → név nélküli üdvözlés —
                „Üdvözöljük, !" sosem jelenhet meg (mérve: a fixture-fióknál épp ez történt). */}
            {greetingName === ''
              ? t('portal.home.greetingNoName')
              : t('portal.home.greeting', { name: greetingName })}
          </h1>
          <Text variant="secondary" as="p" className="mt-2 max-w-[52ch] text-sm leading-relaxed">
            {heroText}
          </Text>
          <div className="mt-5 flex flex-wrap gap-2.5">
            {canOpenTicket && (
              <Button
                variant="brand"
                size="lg"
                onClick={() => void navigate({ to: '/portal/tickets/new' })}
              >
                <PlusIcon />
                {t('portal.newTicket.action')}
              </Button>
            )}
            {canViewTickets && (
              <Button
                variant="outline"
                size="lg"
                onClick={() => void navigate({ to: '/portal/tickets' })}
              >
                <TicketIcon />
                {t('portal.nav.tickets')}
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* KPI-CSEMPÉK — kattinthatók (a szám mellett mindig ott van, hova visz). */}
      <section className="grid gap-x-2 gap-y-4 sm:grid-cols-2 xl:grid-cols-4">
        {tiles
          .filter(
            (tile) =>
              tile.value !== null && (tile.permission === undefined || perms.has(tile.permission)),
          )
          .map((tile) => (
            <Card key={tile.key} className="p-0">
              <button
                type="button"
                onClick={() => void navigate({ to: tile.to })}
                className="hover:bg-accent flex w-full items-center gap-3 p-4 text-left transition-colors"
              >
                <span
                  className={cn(
                    'flex size-[34px] shrink-0 items-center justify-center rounded-[10px] [&>svg]:size-4',
                    tile.tone === 'brand' && 'bg-brand-soft text-brand',
                    tile.tone === 'sky' && 'bg-sky-soft text-sky',
                    tile.tone === 'violet' && 'bg-violet-soft text-violet',
                    tile.tone === 'success' && 'bg-success-soft text-success',
                    tile.tone === 'danger' && 'bg-danger-soft text-danger-600',
                    tile.tone === 'neutral' && 'bg-muted text-muted-foreground',
                  )}
                >
                  <tile.Icon />
                </span>
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="text-[26px] font-semibold tabular-nums leading-none tracking-[-0.025em]">
                    {tile.value}
                  </span>
                  <Text variant="secondary" as="span" className="truncate">
                    {tile.label}
                  </Text>
                </span>
                <ArrowTopRightOnSquareIcon className="text-muted-foreground size-3.5 shrink-0 self-start" />
              </button>
            </Card>
          ))}
      </section>

      {/* LEGUTÓBBI JEGYEIM (2fr) + PÉNZÜGY/VÁLASZRA VÁR (1fr) — a terv `grid-a` rácsa.
          A rács SZÁNDÉKOSAN nyújt (nincs `items-start`): a jegy-lista (max. 3 sor) a „vezető" magasság, a
          jobb hasáb pedig kitölti — így a hasáb alja nem áll csorbán, akár van esedékes díj, akár nincs
          (user-lelet, 2026-09-08). */}
      {(canViewTickets || canViewBilling) && (
        <section
          className={cn(
            'grid gap-x-2 gap-y-4',
            // Két blokknál a terv 2fr/1fr rácsa; ha csak az egyik jogos, egyedül teljes szélességű.
            canViewTickets && canViewBilling && 'lg:grid-cols-[2fr_1fr]',
          )}
        >
          {canViewTickets && (
            <PanelCard
              title={t('portal.home.recentTickets')}
              openAllTo="/portal/tickets"
              loading={recentQ.isLoading}
              skeletonRows={3}
              fill
            >
              {recent.length === 0 ? (
                <EmptyState
                  inset
                  title={t('portal.home.noOpen.title')}
                  description={t('portal.home.noOpen.desc')}
                />
              ) : (
                <CardRows>
                  {recent.map((tk) => (
                    <CardRow key={tk.id}>
                      <button
                        type="button"
                        onClick={() =>
                          void navigate({ to: '/portal/tickets/$id', params: { id: tk.id } })
                        }
                        className="hover:bg-accent flex w-full items-start gap-3 px-4 py-2.5 text-left transition-colors"
                      >
                        {/* A SOR SZERKEZETE a jegy-LISTA „A" változatát követi (user-döntés, 2026-09-08): BAL
                        oldalon a horgony (tárgy) és ALATTA a jelvények — TÍPUS, majd STÁTUSZ, végül az
                        olvasatlan-teendő —, JOBB oldalon a meta (fiók-név, alatta a relatív idő), jobbra
                        zártan. Így a szem egy vonalon fut le a tárgyakon, a „mi ez / hol áll" pár egy
                        helyen van, és a másodlagos adat nem tolja el a címet.
                        A JEGYSZÁM kimarad: a kártya három sort mutat, és a szám itt nem horgony, hanem kód —
                        a listán és a jegy-lapon ott van, a keresőnk pedig illeszt rá. */}
                        <span className="flex min-w-0 flex-1 flex-col gap-1.5">
                          <Text variant="itemTitle" as="span" className="truncate">
                            {tk.subject}
                          </Text>
                          <span className="flex flex-wrap items-center gap-1.5">
                            <TicketTypeBadge
                              type={tk.type}
                              label={t(`portal.type.${tk.type}`, { defaultValue: tk.type })}
                            />
                            <TicketStatusBadge
                              status={tk.status}
                              label={t(`portal.status.${tk.status}`)}
                            />
                            {tk.unreadCommentCount > 0 && (
                              <Badge variant="brand" icon={<ChatBubbleLeftRightIcon />}>
                                {t('portal.tickets.unreadShort', { count: tk.unreadCommentCount })}
                              </Badge>
                            )}
                          </span>
                        </span>
                        {/* META-oszlop: a fiók-név és a relatív idő — `shrink-0`, jobbra zárt, hogy a tárgyak
                        bal éle egy vonalban maradjon. A fiók-név `max-w` alatt vágódik (hosszú név is elfér). */}
                        <span className="flex shrink-0 flex-col items-end gap-0.5 text-right">
                          <Text variant="secondary" as="span" className="max-w-[9rem] truncate">
                            {tk.accountName}
                          </Text>
                          <Text tone="tertiary" as="span" className="whitespace-nowrap text-xs">
                            {formatRelativeTime(tk.lastActivityAt, i18n.language)}
                            {/* a komment-szám ugyanúgy, mint a jegylistán: halvány darabszám az idő mellett,
                            hogy a kártyáról is látszódjon, van-e már válasz (user-lelet) */}
                            {tk.replyCount > 0 &&
                              ` · ${t('portal.tickets.commentCount', { count: tk.replyCount })}`}
                          </Text>
                        </span>
                      </button>
                    </CardRow>
                  ))}
                </CardRows>
              )}
            </PanelCard>
          )}

          {/* JOBB OSZLOP — CSAK a következő havi várható díj. A „Válaszra vár" LEJJEBB, az olvasatlan
            üzenetek MELLÉ került (user-döntés, 2026-09-08): a meghívók nem a pénzügyi blokk alá tartoznak,
            és így mindkét sorban ugyanaz a 2fr/1fr ritmus. */}
          {canViewBilling && (
            <PanelCard title={t('portal.invoices.nextMonth')} loading={servicesQ.isLoading} fill>
              {/* A törzs FLEX-oszlop: az összeg/üzenet fönt marad, a „Szolgáltatásaim" gomb pedig a kártya
                ALJÁRA ül (`mt-auto`) — a megnyúlt kártya így szándékosnak látszik, nem félig üresnek. */}
              <div className="flex h-full flex-col p-4">
                {services === undefined ? (
                  // A szolgáltatás-lekérés hibája NEM viszi magával a lapot: kimondjuk, és a lap többi része áll.
                  <Text variant="secondary" as="p">
                    {t('portal.home.feeUnavailable')}
                  </Text>
                ) : (
                  <>
                    {services.nextMonth.totals.length === 0 ? (
                      <Text variant="secondary" as="p">
                        {t('portal.invoices.noExpected')}
                      </Text>
                    ) : (
                      <div className="space-y-0.5">
                        {services.nextMonth.totals.map((x) => (
                          <div
                            key={x.currency}
                            className="font-mono text-[26px] font-semibold tabular-nums tracking-[-0.03em]"
                          >
                            {formatMoney(x.amount, x.currency, i18n.language)}
                          </div>
                        ))}
                      </div>
                    )}
                    <Text variant="secondary" as="p" className="mt-1.5">
                      {t('portal.home.feeItems', { count: services.nextMonth.items.length })}
                    </Text>
                    {services.nextMonth.hasVariable && (
                      <div className="bg-warning-soft mt-3 flex items-start gap-2.5 rounded-xl p-3 text-[12.5px] leading-relaxed">
                        <InformationCircleIcon className="text-warning mt-0.5 size-4 shrink-0" />
                        <span>{t('portal.invoices.variableWarn')}</span>
                      </div>
                    )}
                    {/* A gomb SAJÁT sávban ül: a `mt-auto` a SÁVON van, nem a gombon — így a gomb belső
                      arányai (magasság, padding) változatlanok, és a fölötte lévő térköz sosem tűnik el. */}
                    <div className="mt-auto pt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => void navigate({ to: '/portal/invoices' })}
                      >
                        {/* A SZOLGÁLTATÁS-ikon (`Square3Stack3DIcon`) — a rendszer „Szolgáltatások" nav-ikonja.
                        A korábbi bankjegy-ikon a PÉNZ jele: a gomb viszont a szolgáltatás-listára visz. */}
                        <Square3Stack3DIcon />
                        {t('portal.invoices.myServices')}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </PanelCard>
          )}
        </section>
      )}

      {/* OLVASATLAN ÜZENETEK — MINDIG látszik, ÜRESEN IS (user-döntés, 2026-09-08): egy felbukkanó-eltűnő
          szekció alatt a lap alja ugrált, és a partner nem tudta, hol keresse az üzeneteit, ha épp nincs
          olvasatlan. Darabszám-jelvény NINCS: a sorok maguk a hír, a fölötte lévő KPI-csempe pedig már számol.
          EGY KIVÉTEL: ha a modul NEM KISZOLGÁLHATÓ (`unavailable` — nincs gépi kredenciál), a szekció kimarad.
          „Nincs olvasatlan üzenete" ilyenkor HAMIS állítás lenne (a `null` nem 0 — ugyanaz a fegyelem, mint a
          KPI-csempéken). */}
      {messages?.unavailable !== true && (
        <section className="grid gap-x-2 gap-y-4 lg:grid-cols-[2fr_1fr]">
          <PanelCard
            title={t('portal.home.unreadMessages')}
            openAllTo="/portal/messages"
            loading={messagesQ.isLoading}
            skeletonRows={2}
            fill
          >
            {unreadMessages.length === 0 ? (
              <EmptyState
                inset
                title={t('portal.home.noUnread.title')}
                description={t('portal.home.noUnread.desc')}
              />
            ) : (
              <CardRows>
                {unreadMessages.map((m) => (
                  <CardRow key={m.id}>
                    <button
                      type="button"
                      onClick={() =>
                        void navigate({ to: '/portal/messages', search: { open: m.id } })
                      }
                      className="hover:bg-accent flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors"
                    >
                      <EnvelopeIcon className="text-danger-600 size-4 shrink-0" />
                      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <Text variant="itemTitle" as="span" className="truncate">
                          {m.subject}
                        </Text>
                        <Text variant="secondary" as="span" className="truncate">
                          {m.from ?? '—'} · {formatRelativeTime(m.sentAt, i18n.language)}
                        </Text>
                      </span>
                    </button>
                  </CardRow>
                ))}
              </CardRows>
            )}
          </PanelCard>

          {/* MEGBESZÉLÉSEK — FIX helye az üzenet-kártya mellett, és üresen is látszik: a felbukkanó-eltűnő
              kártya mozgatta a rácsot, és a partner nem tudta, hol keresse a megbeszéléseit. A kártya KITÖLTI
              a sáv magasságát (`fill`), hogy a sor alja egyenes legyen.
              FEJLÉC-JELVÉNY NINCS: az állapot SORONKÉNT látszik (`MeetingRsvpBadge`) — az „ott leszek /
              nem megyek / válaszra vár" per-esemény információ, egy fejléc-szám nem tudja elmondani. */}
          <PanelCard
            title={t('portal.home.meetings')}
            loading={messagesQ.isLoading}
            skeletonRows={2}
            // FEJLÉC-JELVÉNY NINCS. A kártya SZÁNDÉKOSAN minden fiók megbeszélését mutatja (a meghívó
            // SZEMÉLYRE szól, nem fiókra — egy fiók-szűrő elrejtené a másik fiókon egyeztetett
            // megbeszélést), de ezt NEM írjuk ki „minden fiók" jelvénnyel: a fejléc-zaj többe került, mint
            // amennyit a magyarázat ért (user-döntés — a napközbeni ellenkező döntést váltja).
            fill
          >
            {meetings.length === 0 ? (
              <EmptyState
                inset
                icon={CalendarDaysIcon}
                title={t('portal.home.noMeetings.title')}
                description={t('portal.home.noMeetings.desc')}
              />
            ) : (
              <CardRows>
                {meetings.map((m) => (
                  <CardRow key={m.id}>
                    <button
                      type="button"
                      onClick={() => void navigate({ to: '/portal/messages' })}
                      className="hover:bg-accent flex w-full items-start gap-3 px-4 py-2.5 text-left transition-colors"
                    >
                      <span className="bg-violet-soft text-violet mt-0.5 flex size-[34px] shrink-0 items-center justify-center rounded-[9px] [&>svg]:size-4">
                        <CalendarDaysIcon />
                      </span>
                      <span className="flex min-w-0 flex-1 flex-col gap-1">
                        {/* A NÉV ÉS AZ ÁLLAPOT EGY SORBAN (user-döntés, 2026-09-08): a jelvény közvetlenül a
                            megbeszélés neve után áll, nem a dátum alatt — a jegy-sorok mintája. Szűk kártyán a
                            `flex-wrap` a jelvényt új sorba engedi, a név pedig `truncate`-el (nem tör szét). */}
                        <span className="flex flex-wrap items-center gap-2">
                          <Text variant="itemTitle" as="span" className="min-w-0 truncate">
                            {m.title}
                          </Text>
                          <MeetingRsvpBadge rsvp={m.rsvp} />
                        </span>
                        <Text variant="secondary" as="span" className="truncate">
                          {formatDateTime(m.startsAt, i18n.language)}
                        </Text>
                      </span>
                    </button>
                  </CardRow>
                ))}
              </CardRows>
            )}
          </PanelCard>
        </section>
      )}
    </div>
  );
}
